import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

/** Shared container; each indicator gets a different inner visual. */
const INDICATOR_VISUAL_CLASS = 'relative h-14 w-20 rounded-md overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 border border-slate-200/80 dark:border-slate-600/50';

/** Indicator-specific mini visuals (same size, consistent palette: emerald, sky, indigo, amber). */
function IndicatorVisual({ slug }: { slug: string }) {
  const w = 80;
  const h = 56;
  const pad = 6;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;

  switch (slug) {
    case 'range_analysis': {
      // High–low range bar with current price in the middle
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <rect x={pad} y={pad + 8} width={innerW} height={6} rx={3} fill="currentColor" className="text-sky-400/60 dark:text-sky-500/50" />
            <circle cx={w / 2} cy={pad + 11} r={4} fill="currentColor" className="text-amber-500 dark:text-amber-400" />
          </svg>
        </div>
      );
    }
    case 'simple_moving_average': {
      // Smooth rising trend line
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <path
              d={`M ${pad} ${pad + innerH} L ${pad + 12} ${pad + 32} L ${pad + 28} ${pad + 38} L ${pad + 44} ${pad + 18} L ${pad + 60} ${pad + 24} L ${pad + innerW} ${pad + 8}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-emerald-500 dark:text-emerald-400"
            />
          </svg>
        </div>
      );
    }
    case 'volume_moving_average': {
      // Volume bars of varying height
      const bars = [0.35, 0.6, 0.45, 0.85, 0.5];
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            {bars.map((pct, i) => (
              <rect
                key={i}
                x={pad + 4 + i * (innerW / bars.length)}
                y={pad + innerH - innerH * pct}
                width={innerW / bars.length - 4}
                height={innerH * pct}
                rx={2}
                fill="currentColor"
                className="text-indigo-500/80 dark:text-indigo-400/80"
              />
            ))}
          </svg>
        </div>
      );
    }
    case 'exponential_moving_average': {
      // Two lines: one smoother, one more responsive
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <path
              d={`M ${pad} ${pad + 40} Q ${pad + 20} ${pad + 28} ${pad + 40} ${pad + 22} T ${pad + innerW} ${pad + 12}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="text-sky-400/70 dark:text-sky-400/60"
            />
            <path
              d={`M ${pad} ${pad + 42} L ${pad + 15} ${pad + 20} L ${pad + 35} ${pad + 26} L ${pad + 55} ${pad + 14} L ${pad + innerW} ${pad + 10}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-emerald-500 dark:text-emerald-400"
            />
          </svg>
        </div>
      );
    }
    case 'relative_strength_index': {
      // RSI gauge: 0–100 band with a level
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <rect x={pad} y={pad} width={innerW} height={innerH} rx={4} fill="currentColor" className="text-slate-300/40 dark:text-slate-600/50" />
            <line x1={pad} x2={pad + innerW} y1={pad + innerH / 2} y2={pad + innerH / 2} stroke="currentColor" strokeWidth="1" className="text-slate-400 dark:text-slate-500" />
            <rect x={pad + innerW * 0.55} y={pad + 6} width={innerW * 0.2} height={innerH - 12} rx={2} fill="currentColor" className="text-amber-500 dark:text-amber-400" />
          </svg>
        </div>
      );
    }
    case 'macd': {
      // Small histogram + midline
      const hist = [0.3, 0.5, 0.2, -0.25, -0.4];
      const midY = pad + innerH / 2;
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line x1={pad} x2={pad + innerW} y1={midY} y2={midY} stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" className="text-slate-400 dark:text-slate-500" />
            {hist.map((v, i) => {
              const barW = innerW / hist.length - 2;
              const x = pad + 2 + i * (innerW / hist.length);
              const barH = (innerH / 2 - 4) * Math.abs(v);
              const isPos = v >= 0;
              return (
                <rect
                  key={i}
                  x={x}
                  y={isPos ? midY - barH : midY}
                  width={barW}
                  height={barH}
                  fill="currentColor"
                  className={isPos ? 'text-emerald-500/90 dark:text-emerald-400/90' : 'text-rose-500/90 dark:text-rose-400/90'}
                />
              );
            })}
          </svg>
        </div>
      );
    }
    case 'stochastic_oscillator': {
      // Two oscillating lines in 0–100 style
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <path
              d={`M ${pad} ${pad + 35} L ${pad + 18} ${pad + 12} L ${pad + 36} ${pad + 28} L ${pad + 54} ${pad + 8} L ${pad + innerW} ${pad + 20}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-indigo-500 dark:text-indigo-400"
            />
            <path
              d={`M ${pad} ${pad + 38} L ${pad + 18} ${pad + 18} L ${pad + 36} ${pad + 30} L ${pad + 54} ${pad + 14} L ${pad + innerW} ${pad + 24}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-sky-400/80 dark:text-sky-400/70"
            />
          </svg>
        </div>
      );
    }
    case 'bollinger_bands': {
      // Upper, middle, lower bands with price squiggle
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <path d={`M ${pad} ${pad + 6} L ${pad + innerW} ${pad + 6}`} fill="none" stroke="currentColor" strokeWidth="1" className="text-sky-400/50 dark:text-sky-500/50" />
            <path d={`M ${pad} ${pad + innerH / 2} L ${pad + innerW} ${pad + innerH / 2}`} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-400 dark:text-slate-500" />
            <path d={`M ${pad} ${pad + innerH - 6} L ${pad + innerW} ${pad + innerH - 6}`} fill="none" stroke="currentColor" strokeWidth="1" className="text-sky-400/50 dark:text-sky-500/50" />
            <path
              d={`M ${pad} ${pad + 10} Q ${pad + 20} ${pad + 28} ${pad + 40} ${pad + 14} T ${pad + innerW} ${pad + 22}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="text-amber-500 dark:text-amber-400"
            />
          </svg>
        </div>
      );
    }
    case 'fibonacci_levels': {
      // Horizontal retracement levels
      const levels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            {levels.map((ratio, i) => {
              const y = pad + innerH * (1 - ratio);
              return (
                <line
                  key={i}
                  x1={pad}
                  x2={pad + innerW}
                  y1={y}
                  y2={y}
                  stroke="currentColor"
                  strokeWidth={ratio === 0 || ratio === 1 ? 1.5 : 1}
                  className={ratio === 0 || ratio === 1 ? 'text-slate-500 dark:text-slate-400' : 'text-emerald-500/60 dark:text-emerald-400/60'}
                />
              );
            })}
          </svg>
        </div>
      );
    }
    default: {
      // Fallback: generic mini bars
      return (
        <div className={INDICATOR_VISUAL_CLASS}>
          <div className="absolute inset-2 flex items-end justify-between gap-0.5">
            {[0.4, 0.7, 0.35, 0.9, 0.5].map((pct, i) => (
              <div key={i} className="flex-1 rounded-sm bg-emerald-500/80 dark:bg-emerald-400/80" style={{ height: `${pct * 100}%` }} />
            ))}
          </div>
        </div>
      );
    }
  }
}

interface TechnicalTutorialConfig {
  slug: string;
  categoryName: string;
  shortLabel: string;
  oneLineSummary: string;
}

const TECHNICAL_TUTORIALS: TechnicalTutorialConfig[] = [
  {
    slug: 'range_analysis',
    categoryName: 'Range Analysis',
    shortLabel: 'Range Analysis',
    oneLineSummary: 'Use 52-week highs and lows to quickly see where today’s price sits within its recent trading range.',
  },
  {
    slug: 'simple_moving_average',
    categoryName: 'Simple Moving Average',
    shortLabel: 'Simple Moving Average (SMA)',
    oneLineSummary: 'Smooth out noisy price moves to highlight trends of different durations at a glance.',
  },
  {
    slug: 'volume_moving_average',
    categoryName: 'Volume Moving Average',
    shortLabel: 'Volume Moving Average (VMA)',
    oneLineSummary: 'Spot when trading activity is unusually strong or weak compared to normal volume levels.',
  },
  {
    slug: 'exponential_moving_average',
    categoryName: 'Exponential Moving Average',
    shortLabel: 'Exponential Moving Average (EMA)',
    oneLineSummary: 'React faster to price changes by emphasizing recent data while still tracking the overall trend.',
  },
  {
    slug: 'relative_strength_index',
    categoryName: 'Relative Strength Index',
    shortLabel: 'Relative Strength Index (RSI)',
    oneLineSummary: 'Identify overbought and oversold conditions using a momentum score that ranges from 0 to 100.',
  },
  {
    slug: 'macd',
    categoryName: 'MACD',
    shortLabel: 'MACD',
    oneLineSummary: 'Track how fast a trend is strengthening or weakening using the distance between key moving averages.',
  },
  {
    slug: 'stochastic_oscillator',
    categoryName: 'Stochastic Oscillator',
    shortLabel: 'Stochastic Oscillator',
    oneLineSummary: 'Compare the latest close to its recent trading range to spot short-term turning points.',
  },
  {
    slug: 'bollinger_bands',
    categoryName: 'Bollinger Bands',
    shortLabel: 'Bollinger Bands',
    oneLineSummary: 'See when price is stretched toward support or resistance by tracking how it moves inside dynamic bands.',
  },
  {
    slug: 'fibonacci_levels',
    categoryName: 'Fibonacci Levels',
    shortLabel: 'Fibonacci Levels',
    oneLineSummary: 'Use mathematically-based retracement levels to find potential support, resistance, and pullback zones.',
  },
];

const TechnicalIndicatorsTutorials: React.FC = () => {
  const { fontSize } = useFontSize();

  useEffect(() => {
    document.title = 'Stock Table Tutorials - Technical Indicators';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />

      <main className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <header className="mb-8">
            <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-2`}>
              Technical Indicator Tutorials
            </h1>
            <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary max-w-3xl`}>
              Learn how each technical indicator works, how it is calculated, and how to use it in real trading decisions. 
              These tutorials are designed to match exactly how indicators appear inside Stock Table.
            </p>
          </header>

          <section className="space-y-6">
            <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary`}>
              Explore Technical Indicators
            </h2>

            <div className="grid gap-6 md:grid-cols-2">
              {TECHNICAL_TUTORIALS.map((indicator) => (
                  <Link
                    key={indicator.slug}
                    to={`/tutorials/technicals/${indicator.slug}`}
                    className="group flex flex-col rounded-lg border border-border-light dark:border-border-color bg-surface-light dark:bg-background-card hover:border-primary/70 dark:hover:border-highlight-gold/70 hover:shadow-lg transition-all duration-200 p-4 sm:p-5"
                  >
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex-shrink-0">
                        <IndicatorVisual slug={indicator.slug} />
                      </div>

                      <div className="flex-1">
                        <h3 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary group-hover:text-gold-text-light dark:group-hover:text-highlight-gold`}>
                          {indicator.shortLabel}
                        </h3>
                        <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mt-1`}>
                          {indicator.oneLineSummary}
                        </p>
                        <p className={`${getTextSizeClass(fontSize)} text-gold-text-light dark:text-highlight-gold mt-3 font-medium`}>
                          Read tutorial →
                        </p>
                      </div>
                    </div>
                  </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TechnicalIndicatorsTutorials;

